import type { ChainId } from '@morpho-org/blue-sdk'

type SortableNetworkItem = {
  value: ChainId
  count: number | undefined
}

/**
 * Orders network-filter options by absolute chain TVL (largest first), while pushing chains
 * with zero matching options to the end regardless of TVL ("best of both worlds").
 *
 * Product decision: https://morpholabs.slack.com/archives/C02PAN8UJCQ/p1783070243780889
 * Sort by TVL so chain positions stay stable (muscle memory) as asset/curator/search filters
 * change, but demote chains that currently have 0 results so an active filter that makes a
 * chain irrelevant sinks it to the bottom.
 *
 * `count` is undefined only while facets are still loading; in that state no chain is demoted,
 * so the order is pure TVL desc. `tvlUsd` is absolute (filter-independent), so ordering among
 * chains-with-options does not reshuffle on a checkbox toggle. Until the API ships `tvlUsd`,
 * every TVL resolves to 0 and the stable sort keeps the static config order.
 *
 * Ordering within the demoted (zero-option) tail is best-effort: the Consumer API omits
 * zero-match chains from `facets.chainIds` entirely, so in the real flow those chains have no
 * `tvlUsd` and the tail falls back to static config order. That's intentional — chains that
 * currently have no results only need to sit at the bottom (their relative order is
 * unspecified), and the chains that DO have options are always present in the facet with their
 * absolute `tvlUsd`, so the visible/selectable part of the list is always correctly TVL-ordered.
 * The helper still orders the tail by TVL when a demoted chain's TVL happens to be known.
 */
export function sortNetworkItemsByTvl<T extends SortableNetworkItem>(
  items: T[],
  chainTvls: Map<ChainId, number> | undefined
): T[] {
  return items.toSorted((a, b) => {
    const aEmpty = a.count === 0 ? 1 : 0
    const bEmpty = b.count === 0 ? 1 : 0
    if (aEmpty !== bEmpty) return aEmpty - bEmpty
    return (chainTvls?.get(b.value) ?? 0) - (chainTvls?.get(a.value) ?? 0)
  })
}
